import { useCallback, useEffect, useMemo, useState } from "react";
import type { AddTaskDraft, RailSelection, StartThreadDraft } from "@compositions/index";
import {
  useAddWorkItem,
  useApproveWorkItem,
  useCloseConversation,
  useCompleteWorkItem,
  useEditNote,
  usePinNote,
  useReassignWorkItemQueue,
  useRejectWorkItem,
  useSnoozeWorkItem,
  useSpawnChildWorkflow,
} from "../hooks";
import type { Workflow } from "../types";
import { requiresOutOfQueueCompletionConfirmation } from "../taskCompletionConfirmation";

export interface BoardMutationsParams {
  readonly id: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly workflow: Workflow | undefined;
  readonly activeThread: Workflow | undefined;
  readonly selectedThreadId: string | null;
  /** The task or note that hosts child threads, if the selection is one. */
  readonly selectedThreadHostId: string | null;
  readonly originatingQueueId: string | undefined;
  readonly memberQueueIds: ReadonlySet<string>;
  readonly writeSelection: (
    next: { itemId?: RailSelection; threadId?: string | null },
    options?: { replace?: boolean },
  ) => void;
}

/**
 * Every mutation the board can issue, with the busy id each one exposes.
 * Root-workflow mutations key on `id`; thread mutations key on the
 * selected thread and stay inert until one is open.
 */
export function useBoardMutations({
  id,
  entityType,
  entityId,
  workflow,
  activeThread,
  selectedThreadId,
  selectedThreadHostId,
  originatingQueueId,
  memberQueueIds,
  writeSelection,
}: BoardMutationsParams) {
  const root = { workflowId: id, entityType, entityId };
  const thread = { workflowId: selectedThreadId ?? "", entityType, entityId };

  const rootAddMutation = useAddWorkItem(root);
  const completeMutation = useCompleteWorkItem(root);
  const approveMutation = useApproveWorkItem(root);
  const rejectMutation = useRejectWorkItem(root);
  const snoozeMutation = useSnoozeWorkItem(root);
  const pinMutation = usePinNote(root);
  const editNoteMutation = useEditNote(root);
  const reassignQueueMutation = useReassignWorkItemQueue(root);
  const spawnMutation = useSpawnChildWorkflow();
  const closeRootMutation = useCloseConversation(root);

  const closeThreadMutation = useCloseConversation(thread);
  const threadAddMutation = useAddWorkItem(thread);
  // The thread is its own workflow; its seed task carries
  // `requestedByQueueId = originatingQueueId`, so approve/reject target the
  // thread's id and the board supplies the seed work item id.
  const threadApproveMutation = useApproveWorkItem(thread);
  const threadRejectMutation = useRejectWorkItem(thread);

  // Reply anchors: the most recent routed item in each conversation.
  const rootAnchorId = useMemo<string | undefined>(() => {
    if (workflow === undefined) return undefined;
    for (let i = workflow.workItems.length - 1; i >= 0; i -= 1) {
      const candidate = workflow.workItems[i];
      if (candidate !== undefined && candidate.visibleToQueueIds.length > 0) return candidate.id;
    }
    return undefined;
  }, [workflow]);

  const threadAnchorId = useMemo<string | undefined>(() => {
    if (activeThread === undefined) return undefined;
    for (let i = activeThread.workItems.length - 1; i >= 0; i -= 1) {
      const candidate = activeThread.workItems[i];
      if (candidate !== undefined && candidate.visibleToQueueIds.length > 0) return candidate.id;
    }
    return activeThread.workItems[0]?.id;
  }, [activeThread]);

  const sendGeneralMessage = useCallback(
    (body: string) => {
      rootAddMutation.mutate({
        type: "message",
        body,
        ...(rootAnchorId !== undefined ? { repliedToWorkItemId: rootAnchorId } : {}),
      });
    },
    [rootAddMutation, rootAnchorId],
  );

  const sendThreadMessage = useCallback(
    (body: string) => {
      if (selectedThreadId === null) return;
      threadAddMutation.mutate({
        type: "message",
        body,
        ...(threadAnchorId !== undefined ? { repliedToWorkItemId: threadAnchorId } : {}),
      });
    },
    [threadAddMutation, threadAnchorId, selectedThreadId],
  );

  const startThread = useCallback(
    (draft: StartThreadDraft) => {
      if (
        workflow === undefined ||
        originatingQueueId === undefined ||
        selectedThreadHostId === null
      )
        return;
      spawnMutation.mutate(
        {
          parentWorkflowId: workflow.id,
          parentWorkItemId: selectedThreadHostId,
          entityType: workflow.entityType,
          entityId: workflow.entityId,
          originatingQueueId,
          recipientQueueIds: draft.recipientQueueIds,
          body: draft.body,
        },
        { onSuccess: (child) => writeSelection({ threadId: child.id }) },
      );
    },
    [workflow, originatingQueueId, selectedThreadHostId, spawnMutation, writeSelection],
  );

  const addTask = useCallback(
    (draft: AddTaskDraft) => {
      rootAddMutation.mutate(
        {
          type: "task",
          taskType: draft.taskType,
          body: draft.body,
          ...(draft.dueDate !== undefined ? { dueDate: draft.dueDate } : {}),
          ...(draft.targetQueueId !== undefined ? { targetQueueId: draft.targetQueueId } : {}),
        },
        { onSuccess: (item) => writeSelection({ itemId: item.id, threadId: null }) },
      );
    },
    [rootAddMutation, writeSelection],
  );

  const addNote = useCallback(
    (body: string, parentWorkItemId?: string) => {
      rootAddMutation.mutate(
        { type: "note", body, ...(parentWorkItemId !== undefined ? { parentWorkItemId } : {}) },
        {
          onSuccess: (item) => {
            // A root note becomes the selection. A task-scoped note
            // stays out of the way: the task remains selected and the
            // note appears in its list.
            if (parentWorkItemId === undefined) writeSelection({ itemId: item.id });
          },
        },
      );
    },
    [rootAddMutation, writeSelection],
  );

  // Completion that crosses the task's queue boundary waits for the
  // ConfirmDialog. A confirmation that belongs to a previous workflow drops.
  const [pendingCompletionId, setPendingCompletionId] = useState<string | null>(null);
  useEffect(() => {
    setPendingCompletionId(null);
  }, [id]);

  const completeWorkItem = useCallback(
    (workItemId: string) => {
      const workItem = workflow?.workItems.find((item) => item.id === workItemId);
      if (
        workItem !== undefined &&
        requiresOutOfQueueCompletionConfirmation(workItem, memberQueueIds)
      ) {
        setPendingCompletionId(workItemId);
        return;
      }
      completeMutation.mutate(workItemId);
    },
    [completeMutation, memberQueueIds, workflow],
  );

  const confirmCompletion = useCallback(() => {
    if (pendingCompletionId === null) return;
    completeMutation.mutate(pendingCompletionId);
    setPendingCompletionId(null);
  }, [completeMutation, pendingCompletionId]);

  const cancelCompletion = useCallback(() => setPendingCompletionId(null), []);

  const approveWorkItem = useCallback(
    (workItemId: string) => approveMutation.mutate({ workItemId }),
    [approveMutation],
  );
  const rejectWorkItem = useCallback(
    (workItemId: string, comment: string) => rejectMutation.mutate({ workItemId, comment }),
    [rejectMutation],
  );
  const snoozeWorkItem = useCallback(
    (workItemId: string, newDueDate: string) => snoozeMutation.mutate({ workItemId, newDueDate }),
    [snoozeMutation],
  );
  const pinNote = useCallback(
    (noteId: string, pinned: boolean) => pinMutation.mutate({ workItemId: noteId, pinned }),
    [pinMutation],
  );
  const editNote = useCallback(
    (noteId: string, body: string) => editNoteMutation.mutate({ workItemId: noteId, body }),
    [editNoteMutation],
  );
  const reassignWorkItemQueue = useCallback(
    (workItemId: string, currentQueueIds: readonly string[], newQueueId: string) =>
      reassignQueueMutation.mutate({ workItemId, currentQueueIds, newQueueId }),
    [reassignQueueMutation],
  );
  const approveThreadAnswer = useCallback(
    (workItemId: string) => {
      if (selectedThreadId !== null) threadApproveMutation.mutate({ workItemId });
    },
    [threadApproveMutation, selectedThreadId],
  );
  const rejectThreadAnswer = useCallback(
    (workItemId: string, comment: string) => {
      if (selectedThreadId !== null) threadRejectMutation.mutate({ workItemId, comment });
    },
    [threadRejectMutation, selectedThreadId],
  );
  const closeConversation = useCallback(() => closeRootMutation.mutate(), [closeRootMutation]);
  const closeThread = useCallback(() => {
    if (selectedThreadId !== null) closeThreadMutation.mutate();
  }, [closeThreadMutation, selectedThreadId]);

  const busyId = (pending: boolean, workItemId: string | undefined): string | null =>
    pending ? (workItemId ?? null) : null;

  return {
    handlers: {
      sendGeneralMessage,
      sendThreadMessage,
      startThread,
      addTask,
      addNote,
      completeWorkItem,
      confirmCompletion,
      cancelCompletion,
      approveWorkItem,
      rejectWorkItem,
      snoozeWorkItem,
      pinNote,
      editNote,
      reassignWorkItemQueue,
      approveThreadAnswer,
      rejectThreadAnswer,
      closeConversation,
      closeThread,
    },
    pendingCompletionId,
    busy: {
      isSending: rootAddMutation.isPending || threadAddMutation.isPending,
      isStartingThread: spawnMutation.isPending,
      isClosingConversation: closeRootMutation.isPending,
      isClosingThread: closeThreadMutation.isPending,
      isCompletingId: completeMutation.isPending ? (completeMutation.variables ?? null) : null,
      isApprovingId: busyId(approveMutation.isPending, approveMutation.variables?.workItemId),
      isRejectingId: busyId(rejectMutation.isPending, rejectMutation.variables?.workItemId),
      isSnoozingId: busyId(snoozeMutation.isPending, snoozeMutation.variables?.workItemId),
      isPinningId: busyId(pinMutation.isPending, pinMutation.variables?.workItemId),
      isEditingNoteId: busyId(editNoteMutation.isPending, editNoteMutation.variables?.workItemId),
      isReassigningQueueId: busyId(
        reassignQueueMutation.isPending,
        reassignQueueMutation.variables?.workItemId,
      ),
      isApprovingThreadAnswerId: busyId(
        threadApproveMutation.isPending,
        threadApproveMutation.variables?.workItemId,
      ),
      isRejectingThreadAnswerId: busyId(
        threadRejectMutation.isPending,
        threadRejectMutation.variables?.workItemId,
      ),
    },
  };
}
